import type { ComponentPropsWithoutRef, ReactNode, Ref } from 'react';
import { cx } from '../../utils/cx';
import { useFieldProps } from '../FormField/FormField';
import s from './TextInput.module.css';

export type TextInputProps = Omit<ComponentPropsWithoutRef<'input'>, 'prefix'> & {
  ref?: Ref<HTMLInputElement>;
  /** Inline content before the value, e.g. "$" or an icon. Decorative. */
  prefix?: ReactNode;
  /** Inline content after the value, e.g. a unit or a clear button. */
  suffix?: ReactNode;
};

/**
 * Single-line input. Pass the right `type`/`inputMode` for mobile keyboards
 * (`type="url"`, `inputMode="decimal"` for prices, `autoComplete`, …).
 */
export function TextInput({ prefix, suffix, className, type = 'text', ...rest }: TextInputProps) {
  const props = useFieldProps(rest);
  if (!prefix && !suffix)
    return <input type={type} className={cx(s.input, className)} {...props} />;
  return (
    <div className={cx(s.wrap, className)} data-invalid={props['aria-invalid'] ? true : undefined}>
      {prefix ? (
        <span className={s.affix} aria-hidden="true">
          {prefix}
        </span>
      ) : null}
      <input type={type} className={s.bare} {...props} />
      {suffix ? <span className={s.affix}>{suffix}</span> : null}
    </div>
  );
}
